
import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Check, Loader2, CheckCircle, Calendar, CreditCard, Award, FileText, X, Upload } from 'lucide-react';
import certificadoImg from '../kardex/Certificado_Cursos_Extensión2.png';

/**
 * TalleresEstudiante
 * Vista para estudiantes de cursos de extensión.
 * Incluye flujo de inscripción con opción de certificado y pagos.
 */
const TalleresEstudiante = () => {
    const [cursos, setCursos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [error, setError] = useState(null);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedCurso, setSelectedCurso] = useState(null);
    const [modalStep, setModalStep] = useState(1); // 1: Pregunta certificado, 2: Pago
    const [formData, setFormData] = useState({
        numero_cuotas: '1',
        certificado_requerido: null
    });
    const [comprobante, setComprobante] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [paymentConfig, setPaymentConfig] = useState(null);
    const [tipoPago, setTipoPago] = useState('qr'); // qr | transferencia

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

    const [nombreCompleto, setNombreCompleto] = useState('');
    const [loadingName, setLoadingName] = useState(true);

    useEffect(() => {
        const fetchFullName = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                const token = localStorage.getItem('token');
                if (!user.id_usuario) return;

                const response = await fetch(`${API_URL}/estudiante/info-matriculacion?id_usuario=${user.id_usuario}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.ok) {
                    const data = await response.json();
                    const est = data.estudiante || {};
                    const full = [
                        est.nombres,
                        est.apellido_paterno,
                        est.apellido_materno
                    ].filter(Boolean).join(' ');

                    setNombreCompleto(full || user.nombre_completo || user.username || '');
                } else {
                    // Fallback to local storage if API fails
                    setNombreCompleto(user.nombre_completo || `${user.nombres || ''} ${user.apellido || ''}`.trim() || user.username || '');
                }
            } catch (e) {
                console.error('Error fetching full name:', e);
            } finally {
                setLoadingName(false);
            }
        };

        fetchFullName();
    }, [API_URL]);

    // Verificar si la inscripción está habilitada (Parametrización ID 4)
    const [inscripcionHabilitada, setInscripcionHabilitada] = useState(false);
    useEffect(() => {
        const checkParam = async () => {
            try {
                const res = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/4`);
                const d = await res.json();
                setInscripcionHabilitada(d.habilitado);
            } catch (e) {
                console.error(e);
                setInscripcionHabilitada(false);
            }
        };
        checkParam();
    }, [API_URL]);

    const fetchCursos = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/estudiante/cursos-extension`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Error al cargar cursos');

            const data = await response.json();
            // Ordenar por id_curso DESC (recientes primero)
            const sorted = (data.cursos || []).sort((a, b) => (b.id_curso || 0) - (a.id_curso || 0));
            setCursos(sorted);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [API_URL]);

    const loadPaymentConfig = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/direccion/pagos/configuraciones`);
            const data = await response.json();
            // Buscar config de curso_extension (id=2 o tipo compatible)
            const config = (data.configuraciones || []).find(c => c.tipo === 'curso_extension') || data.configuraciones?.[0];
            setPaymentConfig({
                qr_image: config.qr_image_path,
                banco: config.banco,
                numero_cuenta: config.numero_cuenta,
                titular: config.titular,
                nit: config.nit,
                qr_activo: config.qr_activo !== false,
                banco_activo: config.banco_activo !== false
            });

            // Auto-seleccionar primer método disponible
            if (config.qr_activo !== false) {
                setTipoPago('qr');
            } else if (config.banco_activo !== false) {
                setTipoPago('transferencia');
            }
        } catch (e) {
            console.error('Error loading payment config:', e);
        }
    }, [API_URL]);

    useEffect(() => {
        fetchCursos();
        loadPaymentConfig();
    }, [fetchCursos, loadPaymentConfig]);

    const handleInscribirseClick = async (curso) => {
        // Verificación dinámica justo antes de abrir el modal
        try {
            const res = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/4`);
            const d = await res.json();
            if (!d.habilitado) {
                setInscripcionHabilitada(false);
                alert('El periodo de inscripción ha sido cerrado por Dirección Académica.');
                return;
            }
        } catch (e) { console.error(e); }

        if (isDeadlinePassed(curso)) {
            alert('El plazo de inscripción para este curso ha vencido.');
            return;
        }

        setSelectedCurso(curso);
        setFormData({ numero_cuotas: '1', certificado_requerido: null });
        setComprobante(null);
        setModalStep(1);
        setShowModal(true);
    };

    const handleCertificadoSelect = (value) => {
        const wantsCertificate = value === 'si';
        setFormData(prev => ({ ...prev, certificado_requerido: wantsCertificate }));

        if (!wantsCertificate) {
            // Si NO quiere certificado, procedemos a inscribir gratis directamente
            submitInscripcion(false);
        } else {
            // Si SI quiere certificado, vamos al paso de pago
            setModalStep(2);
        }
    };

    const submitInscripcion = async (conCertificado) => {
        if (!selectedCurso) return;
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const dataToSend = new FormData();
            dataToSend.append('id_curso', selectedCurso.id_curso);
            dataToSend.append('numero_cuotas', conCertificado ? formData.numero_cuotas : '1');
            dataToSend.append('certificado_requerido', conCertificado);

            if (conCertificado && comprobante) {
                dataToSend.append('comprobante_pago', comprobante);
                dataToSend.append('tipo_pago', tipoPago);
            }

            const response = await fetch(`${API_URL}/estudiante/cursos-extension/inscribir`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: dataToSend
            });

            const data = await response.json();

            if (!response.ok) throw new Error(data.message || 'Error al inscribirse');

            alert(conCertificado ? '¡Inscripción exitosa! Tu solicitud de certificado será procesada.' : '¡Inscripción exitosa! Te has inscrito al curso.');
            setShowModal(false);
            fetchCursos();
        } catch (err) {
            alert(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDescargarCertificado = (nombreCurso = 'Curso de Extensión', modulos) => {
        // 1. Lógica para encontrar la fecha más reciente (la última)
        const obtenerUltimaFecha = (listaModulos) => {
            if (!listaModulos || !Array.isArray(listaModulos) || listaModulos.length === 0) return null;

            // Convertimos las fechas a timestamps para comparar correctamente
            const timestamps = listaModulos
                .map(m => new Date(m.fecha_fin).getTime())
                .filter(ts => !isNaN(ts)); // Filtramos valores inválidos

            if (timestamps.length === 0) return null;

            return new Date(Math.max(...timestamps));
        };

        const ultimaFecha = obtenerUltimaFecha(modulos);

        // 2. Formatear la fecha final encontrada
        const fechaTexto = ultimaFecha
            ? ultimaFecha.toLocaleDateString('es-BO', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                timeZone: 'UTC' // <--- FORZAR UTC para que no se mueva el día
            })
            : 'la fecha establecida';

        // 3. Preparación de otros datos
        const toTitleCase = (str) =>
            str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

        const nombreParticipante = toTitleCase(nombreCompleto || 'Nombre Del Participante');
        const fechaHoy = new Date().toLocaleDateString('es-BO', {
            month: 'long',
            year: 'numeric'
        });
        const fechaFormateada = `El Alto, ${fechaHoy}`;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="es">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Certificado - ${nombreCurso}</title>
            <link href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Montserrat:wght@700;900&family=Open+Sans:ital,wght@0,400;1,400&display=swap" rel="stylesheet">
            <style>
                @page { size: landscape; margin: 0; }
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: 'Times New Roman', Times, serif; background-color: white; display: flex; justify-content: center; align-items: center; height: 100vh; }
                
                .certificate-container { width: 297mm; height: 210mm; position: relative; display: flex; flex-direction: column; align-items: center; padding: 28mm 30mm 10mm 30mm; }
                .header { text-align: center; margin-bottom: 30px; }
                .titulo {  sans-serif; font-size: 42pt; font-weight: 900; color: #1a2e6e; letter-spacing: 3px; text-transform: uppercase; line-height: 1; }
                .subtitulo { font-size: 20pt; font-weight: 700; color: #1a2e6e; letter-spacing: 5px; text-transform: uppercase; display: flex; align-items: center; gap: 8px; }
                .subtitulo::before, .subtitulo::after { content: ''; display: inline-block; width: 45px; height: 2px; background-color: #1a2e6e; }
                .se-otorga { font-family: 'Open Sans', sans-serif; font-style: italic; font-size: 12pt; color: #333; align-self: flex-start; margin-top: 8mm; }
                .nombre { font-family: 'Great Vibes', cursive; font-size: 44pt; color: #1a2e6e; border-bottom: 1.5px solid #1a2e6e; padding-bottom: 1mm; margin-top: 1mm; text-align: center; min-width: 140mm; }
                .details { font-size: 18px; line-height: 1.6; max-width: 80%; margin: 10px 0 0 20px; align-self: flex-start; }
                .fecha { font-size: 11pt; font-weight: 700; align-self: flex-start; margin-top: 4mm; }
                .footer { display: flex; justify-content: space-around; width: 100%; margin-top: auto; padding-bottom: 10mm; }
                .signature-box { width: 250px; text-align: center; }
                .signature-line { border-top: 2px solid #333; padding-top: 5px; font-weight: bold; font-size: 14px; text-transform: uppercase; }
                .bicentenario { position: absolute; bottom: 5mm; left: 50%; transform: translateX(-50%); font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 9pt; color: #1a2e6e; }
                .btn-print { position: fixed; bottom: 20px; right: 20px; padding: 12px 28px; background: #1a2e6e; color: white; border: none; border-radius: 8px; font-weight: bold; cursor: pointer; z-index: 9999; }
                @media print { .btn-print { display: none; } }
            </style>
        </head>
        <body>
            <button class="btn-print" onclick="window.print()">Descargar como PDF</button>
            <div class="certificate-container">
                <div class="header">
                    <div class="titulo">CERTIFICADO</div>
                    <div class="subtitulo">DE PARTICIPACIÓN</div>
                </div>
                <div class="se-otorga">Se otorga a:</div>
                <div class="nombre">${nombreParticipante}</div>
                <div class="details">
                    Por haber participado en el:<br>
                    <strong>CURSO DE EXTENSIÓN DE ${nombreCurso.toUpperCase()}</strong><br>
                    Desarrollado en las instalaciones de la Escuela Boliviana Intercultural de Danza en el periodo de ${fechaTexto}.
                </div>
                <div class="fecha">${fechaFormateada}</div>
                <div class="footer">
                    <div class="signature-box"><div class="signature-line">Facilitador</div></div>
                    <div class="signature-box"><div class="signature-line">Jefatura de Carrera</div></div>
                </div>
                <div class="bicentenario">"2025 AÑO DEL BICENTENARIO"</div>
            </div>
        </body>
        </html>
        `);
        printWindow.document.close();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        // Usar UTC para evitar desfase de zona horaria
        const date = new Date(dateStr + (dateStr.includes('T') ? '' : 'T12:00:00'));
        return date.toLocaleDateString('es-BO', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/La_Paz' });
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '-';
        return timeStr.substring(0, 5);
    };

    const parseModulos = (modulos) => {
        if (!modulos) return [];
        if (typeof modulos === 'string') {
            try {
                return JSON.parse(modulos);
            } catch {
                return [];
            }
        }
        return Array.isArray(modulos) ? modulos : [];
    };

    const formatCurrency = (amount) => {
        if (!amount) return 'Bs. 0';
        return `Bs. ${parseFloat(amount).toFixed(2)}`;
    };

    const isDeadlinePassed = (curso) => {
        if (!curso) return false;
        const deadline = curso.fecha_limite_inscripcion || curso.fecha_inicio;
        if (!deadline) return false;
        const deadlineDate = new Date(deadline);
        deadlineDate.setHours(23, 59, 59, 999);
        return new Date() > deadlineDate;
    };


    const renderInscriptionButton = (curso) => {
        const inscrito = curso.inscrito || curso.estado_boton === 'inscrito' || curso.estado_boton === 'descargar_certificado';
        const sinCupo = curso.disponibles !== undefined && curso.disponibles !== null && curso.disponibles <= 0;

        if (inscrito) {
            return (
                <button
                    disabled
                    className="shrink-0 px-6 py-2.5 rounded-lg font-semibold bg-green-100 text-green-700 flex items-center gap-2 cursor-default"
                >
                    <Check className="w-5 h-5" />
                    Inscrito
                </button>
            );
        }

        if (sinCupo) {
            return (
                <button
                    disabled
                    className="shrink-0 px-6 py-2.5 rounded-lg font-semibold bg-red-100 text-red-600 flex items-center gap-2 cursor-not-allowed"
                >
                    <X className="w-5 h-5" />
                    Sin Cupo
                </button>
            );
        }

        if (isDeadlinePassed(curso) || !inscripcionHabilitada) {
            return (
                <button
                    disabled
                    className="shrink-0 px-6 py-2.5 rounded-lg font-semibold bg-gray-100 text-gray-500 cursor-not-allowed"
                >
                    {isDeadlinePassed(curso) ? 'Plazo Vencido' : 'Periodo Cerrado'}
                </button>
            );
        }

        return (
            <button
                onClick={() => handleInscribirseClick(curso)}
                className="shrink-0 px-6 py-2.5 rounded-lg font-semibold transition flex items-center gap-2 bg-[#1e3a8a] text-white hover:bg-[#2c4a9a]"
            >
                <BookOpen className="w-5 h-5" />
                Inscribirse
            </button>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Cargando cursos...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="bg-red-50 text-red-700 p-4 rounded-lg">
                    Error: {error}
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 overflow-hidden">
            {/* Header */}
            <div className="mb-4">
                <h1 className="text-xl font-bold text-gray-800">
                    Estudiante &gt; Cursos de Extensión
                </h1>
            </div>
            <div className="mb-4">



                {/* Courses List */}
                {cursos.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                        <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No hay cursos disponibles en este momento</p>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {cursos.map((curso) => {
                            const modulos = parseModulos(curso.modulos);

                            return (
                                <div
                                    key={curso.id_curso}
                                    className="bg-white rounded-2xl shadow-lg overflow-hidden"
                                >
                                    {/* Header con gradiente */}
                                    <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-400 h-16 flex items-center justify-center">
                                        <Award size={32} className="text-white/80" />
                                    </div>

                                    {/* Contenido del card */}
                                    <div className="p-6">
                                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                                            <div>
                                                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                                                    {curso.nombre}
                                                </h3>
                                                <p className="text-gray-600 text-sm">
                                                    {curso.descripcion || 'Sin descripción disponible'}
                                                </p>
                                            </div>

                                            {renderInscriptionButton(curso)}
                                        </div>

                                        {/* Info del curso */}
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                <Calendar size={18} className="text-blue-500" />
                                                <span><strong>Inicio:</strong> {formatDate(curso.fecha_inicio)}</span>
                                            </div>

                                            {/* FIX: Aviso de sin cupo cuando no hay disponibilidad */}
                                            {curso.disponibles !== undefined && curso.disponibles !== null && curso.disponibles <= 0 ? (
                                                <div className="flex items-center gap-2 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                                    <X size={18} className="text-red-500" />
                                                    <span className="font-medium text-red-700"><strong>Aviso:</strong> No hay cupo disponible para este curso</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 text-sm text-gray-700">
                                                    <CheckCircle size={18} className={curso.disponibles > 0 ? "text-green-500" : "text-red-500"} />
                                                    <span>
                                                        <strong>Cupos:</strong> {
                                                            curso.disponibles !== undefined && curso.disponibles !== null
                                                                ? curso.disponibles
                                                                : (curso.capacidad || '30')
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Tabla de Módulos */}
                                        {modulos.length > 0 && (
                                            <div className="mb-6">
                                                <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                                                    Módulos del Curso
                                                </h4>
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-sm">
                                                        <thead>
                                                            <tr className="text-left text-gray-600 border-b">
                                                                <th className="py-2 px-2 font-medium">Modulo</th>
                                                                <th className="py-2 px-2 font-medium">Duración</th>
                                                                <th className="py-2 px-2 font-medium">Carga Horaria</th>
                                                                <th className="py-2 px-2 font-medium">Sesiones</th>
                                                                <th className="py-2 px-2 font-medium">Fecha</th>
                                                                <th className="py-2 px-2 font-medium">Días</th>
                                                                <th className="py-2 px-2 font-medium">Hora</th>
                                                                <th className="py-2 px-2 font-medium">Docente</th>
                                                                <th className="py-2 px-2 font-medium">Sala</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {modulos.map((mod, idx) => (
                                                                <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                                                                    <td className="py-2 px-2 font-medium text-gray-900">{mod.nombre || '-'}</td>
                                                                    <td className="py-2 px-2 text-gray-600">{mod.duracion ? `${mod.duracion} hrs` : '-'}</td>
                                                                    <td className="py-2 px-2 text-gray-600">{mod.carga_horaria ? `${mod.carga_horaria} hrs` : '-'}</td>
                                                                    <td className="py-2 px-2 text-gray-600">{mod.sesiones || '-'}</td>
                                                                    <td className="py-2 px-2 text-gray-600">
                                                                        {mod.fecha_inicio && mod.fecha_fin
                                                                            ? `${formatDate(mod.fecha_inicio)} - ${formatDate(mod.fecha_fin)}`
                                                                            : formatDate(mod.fecha_inicio) || '-'}
                                                                    </td>
                                                                    <td className="py-2 px-2 text-gray-600 capitalize">{mod.dias?.replace(/,/g, ', ') || '-'}</td>
                                                                    <td className="py-2 px-2 text-gray-600">
                                                                        {mod.hora_inicio && mod.hora_fin
                                                                            ? `${formatTime(mod.hora_inicio)} - ${formatTime(mod.hora_fin)}`
                                                                            : '-'}
                                                                    </td>
                                                                    <td className="py-2 px-2 text-gray-600">{mod.facilitador || '-'}</td>
                                                                    <td className="py-2 px-2 text-gray-600">{mod.sala || '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Botón Descargar Certificado (si tiene permiso) */}
                                        <button
                                            onClick={() => handleDescargarCertificado(curso.nombre, parseModulos(curso.modulos))}
                                            disabled={curso.estado_boton !== 'descargar_certificado'}
                                            className={`w-full py-3 px-4 rounded-lg font-semibold transition flex items-center justify-center gap-2 border ${curso.estado_boton === 'descargar_certificado'
                                                ? 'bg-white border-emerald-500 text-emerald-600 hover:bg-emerald-50'
                                                : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                                }`}
                                        >
                                            <FileText className="w-5 h-5" />
                                            Descargar certificado
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal de Inscripción */}
            {showModal && selectedCurso && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="text-xl font-bold text-blue-900">Inscripción al Curso</h3>
                            <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="bg-blue-50 rounded-lg p-4 mb-4">
                                <h4 className="font-semibold text-blue-800">{selectedCurso.nombre}</h4>
                            </div>

                            {/* PASO 1: Preguntar certificado */}
                            {modalStep === 1 && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            ¿Desea que le emitan un certificado del curso?
                                        </label>
                                        <p className="text-xs text-gray-500 mb-3 block">
                                            El certificado tiene un costo adicional. Si selecciona "No", la inscripción es gratuita.
                                        </p>
                                        <select
                                            onChange={(e) => e.target.value && handleCertificadoSelect(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            defaultValue=""
                                            disabled={submitting}
                                        >
                                            <option value="" disabled>Seleccione una opción</option>
                                            <option value="si">Sí, quiero certificado (Tiene costo)</option>
                                            <option value="no">No, inscribirme sin certificado (Gratis)</option>
                                        </select>
                                    </div>
                                    {submitting && (
                                        <div className="text-center py-4 text-blue-600 flex items-center justify-center gap-2">
                                            <Loader2 className="animate-spin w-4 h-4" /> Procesando...
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* PASO 2: Pago */}
                            {modalStep === 2 && (
                                <div>
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                                        <p className="text-sm text-yellow-800">
                                            <span className="font-medium">Precio Curso:</span> {formatCurrency(selectedCurso.costo || selectedCurso.costo_certificado)}
                                        </p>
                                    </div>

                                    {paymentConfig && (
                                        <div className="space-y-4 mb-6">
                                            {/* Selector de Método de Pago - Solo si AMBOS están activos */}
                                            {paymentConfig.qr_activo && paymentConfig.banco_activo ? (
                                                <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                                                    <button
                                                        type="button"
                                                        onClick={() => setTipoPago('qr')}
                                                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${tipoPago === 'qr' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                                    >
                                                        Código QR
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setTipoPago('transferencia')}
                                                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${tipoPago === 'transferencia' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                                    >
                                                        Transferencia
                                                    </button>
                                                </div>
                                            ) : (!paymentConfig.qr_activo && !paymentConfig.banco_activo) && (
                                                <div className="bg-rose-50 border-2 border-rose-200 rounded-2xl p-6 text-center animate-in zoom-in duration-300">
                                                    <div className="text-3xl mb-3 text-rose-500">💳❌</div>
                                                    <h4 className="text-lg font-black text-rose-900 uppercase tracking-tight mb-2">
                                                        Pagos Inhabilitados
                                                    </h4>
                                                    <p className="text-rose-700 text-xs font-medium">
                                                        Actualmente no se están recibiendo pagos digitales.
                                                        Por favor, contacte con administración.
                                                    </p>
                                                </div>
                                            )}

                                            {/* SECCIÓN QR: Mostrar si está activo Y es el seleccionado (o si es el único activo) */}
                                            {paymentConfig.qr_activo && (tipoPago === 'qr' || !paymentConfig.banco_activo) && (
                                                <div className="p-4 bg-white border rounded-xl text-center animate-in fade-in duration-300">
                                                    <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Escanear para pagar</h5>
                                                    {paymentConfig.qr_image ? (
                                                        <img src={paymentConfig.qr_image} alt="QR" className="mx-auto max-h-48 object-contain rounded-lg shadow-sm border border-gray-100" />
                                                    ) : (
                                                        <div className="py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                                            <CreditCard className="mx-auto text-gray-300 mb-2" />
                                                            <p className="text-xs text-gray-400">QR no disponible</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* SECCIÓN TRANSFERENCIA: Mostrar si está activo Y es el seleccionado (o si es el único activo) */}
                                            {paymentConfig.banco_activo && (tipoPago === 'transferencia' || !paymentConfig.qr_activo) && (
                                                <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-xl animate-in fade-in duration-300">
                                                    <h5 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-3">Datos de depósito</h5>
                                                    <div className="space-y-2 text-sm">
                                                        <div className="flex justify-between border-b border-blue-100/50 pb-1">
                                                            <span className="text-gray-500">Banco:</span>
                                                            <span className="font-bold text-gray-800">{paymentConfig.banco}</span>
                                                        </div>
                                                        <div className="flex justify-between border-b border-blue-100/50 pb-1">
                                                            <span className="text-gray-500">N° Cuenta:</span>
                                                            <span className="font-mono font-bold text-blue-700">{paymentConfig.numero_cuenta}</span>
                                                        </div>
                                                        <div className="flex justify-between border-b border-blue-100/50 pb-1">
                                                            <span className="text-gray-500">Titular:</span>
                                                            <span className="font-medium text-gray-800">{paymentConfig.titular}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-500">NIT/CI:</span>
                                                            <span className="font-medium text-gray-800">{paymentConfig.nit}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}


                                    <form onSubmit={(e) => { e.preventDefault(); submitInscripcion(true); }} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Número de Cuotas</label>
                                            <select
                                                value={formData.numero_cuotas}
                                                onChange={(e) => setFormData(prev => ({ ...prev, numero_cuotas: e.target.value }))}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                                required
                                            >
                                                <option value="1">1 cuota (Pago único)</option>
                                                {selectedCurso.numero_cuotas > 1 && <option value="2">2 cuotas</option>}
                                                {selectedCurso.numero_cuotas > 2 && <option value="3">3 cuotas</option>}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Comprobante de Pago</label>
                                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:bg-gray-50">
                                                <input
                                                    type="file"
                                                    onChange={(e) => setComprobante(e.target.files[0])}
                                                    accept="image/*,.pdf"
                                                    className="hidden"
                                                    id="file-upload"
                                                    required
                                                />
                                                <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                                                    <Upload className="w-6 h-6 text-gray-400 mb-2" />
                                                    <span className="text-sm text-gray-600">
                                                        {comprobante ? comprobante.name : 'Click para subir comprobante'}
                                                    </span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className="flex justify-end gap-3 pt-4">
                                            <button
                                                type="button"
                                                onClick={() => setModalStep(1)}
                                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                            >
                                                Atrás
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                className="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50"
                                            >
                                                {submitting ? 'Enviando...' : 'Confirmar con Pago'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TalleresEstudiante;
